/*
 * Watch scroll reveal — scroll-scrubbed video.
 *
 * Mobile notes (why this is built the way it is):
 *  - The video is shown as soon as its metadata is known (never hidden
 *    waiting on 'loadeddata', which iOS may not fire until playback).
 *  - A new seek is only issued once the previous one has finished ('seeked'),
 *    so phones never get a queue of seeks they can't keep up with.
 *  - The video is muted + playsinline, so a play()->pause() "prime" is allowed
 *    without a tap on iOS/Android. A tap fallback remains for Low Power Mode.
 *  - On small screens the caption fades out as the journey starts, so the
 *    video isn't hidden behind the text box.
 */
(function () {
  function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
  }

  function init(root) {
    if (root.__watchRevealInit) return;
    root.__watchRevealInit = true;

    var video = root.querySelector('.watch-reveal__video');
    var caption = root.querySelector('.watch-reveal__caption');
    if (!video) return;

    var srcUrl = video.getAttribute('data-src') || video.getAttribute('src');
    if (!srcUrl) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', 'true');

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var smallMQ = window.matchMedia('(max-width: 860px)');

    if (reduce) {
      // Static frame only.
      video.preload = 'auto';
      video.src = srcUrl.split('#')[0] + '#t=0.5';
      return;
    }

    var ready = false;
    var primed = false;
    var target = 0;
    var current = 0;
    var seeking = false;
    var seekStarted = 0;
    var lastSet = -1;
    var inView = true;
    var rafId = null;
    var scrollTicking = false;

    function load() {
      // Load the file directly (not via a blob: URL — iOS Safari is unreliable
      // with those). "#t=0.001" makes iOS decode and paint the first frame.
      if (video.getAttribute('src')) return; // already loading from the markup
      video.preload = 'auto';
      video.src = srcUrl.indexOf('#') === -1 ? srcUrl + '#t=0.001' : srcUrl;
      try { video.load(); } catch (e) {}
    }

    function prime() {
      if (primed || !ready) return;
      var p;
      try { p = video.play(); } catch (e) {}
      if (p && p.then) {
        p.then(function () {
          primed = true;
          video.pause();
          lastSet = -1; // force a repaint at the right position
          start();
        }).catch(function () {
          // Autoplay refused (e.g. iOS Low Power Mode) — wait for a tap.
        });
      } else {
        primed = true;
        try { video.pause(); } catch (e) {}
      }
    }

    function onGesture() {
      prime();
      if (primed) {
        window.removeEventListener('touchstart', onGesture);
        window.removeEventListener('touchend', onGesture);
        window.removeEventListener('click', onGesture);
      }
    }
    window.addEventListener('touchstart', onGesture, { passive: true });
    window.addEventListener('touchend', onGesture, { passive: true });
    window.addEventListener('click', onGesture, { passive: true });

    function onReady() {
      if (ready) return;
      ready = true;
      try { video.pause(); } catch (e) {}
      root.classList.add('is-ready');
      prime();
      start();
    }
    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);
    if (video.readyState >= 1) onReady();

    video.addEventListener('seeked', function () {
      seeking = false;
    });

    function read() {
      var rect = root.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      target = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;
      scrollTicking = false;
      start();
    }

    function updateCaption() {
      if (!caption) return;
      if (smallMQ.matches) {
        // Fade the caption out over the first ~15% of the journey.
        var o = 1 - clamp((current - 0.04) / 0.12, 0, 1);
        caption.style.opacity = o.toFixed(3);
        caption.style.transform = 'translateY(' + ((1 - o) * -16).toFixed(1) + 'px)';
        caption.style.visibility = o < 0.01 ? 'hidden' : '';
      } else {
        caption.style.opacity = '';
        caption.style.transform = '';
        caption.style.visibility = '';
      }
    }

    function tick() {
      rafId = null;

      var diff = target - current;
      current = Math.abs(diff) < 0.0005 ? target : current + diff * 0.15;

      // Safety net: some mobile browsers occasionally never fire 'seeked'.
      if (seeking && performance.now() - seekStarted > 300) seeking = false;

      if (ready && !seeking) {
        var d = video.duration;
        if (d && isFinite(d)) {
          var t = clamp(current * d, 0, d - 0.05);
          if (Math.abs(t - lastSet) > 0.02) {
            seeking = true;
            seekStarted = performance.now();
            lastSet = t;
            try { video.currentTime = t; } catch (e) { seeking = false; }
          }
        }
      }

      updateCaption();

      var settled = current === target && !seeking && Math.abs((video.currentTime || 0) - lastSet) < 0.05;
      if (inView && !settled) start();
    }

    function start() {
      if (rafId == null) rafId = requestAnimationFrame(tick);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!scrollTicking) {
          scrollTicking = true;
          requestAnimationFrame(read);
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', read, { passive: true });
    window.addEventListener('orientationchange', read, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView) read();
      }).observe(root);
    }

    load();
    read();
  }

  function boot() {
    document.querySelectorAll('[data-watch-reveal]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
